/**
 * BiblioSourceDetailsPanel Component
 *
 * An inline side panel that displays the full details of a selected
 * biblio source (journal article, report, or any other source type). Uses the
 * generic DetailsSidePanel for layout and close-button behavior.
 */

import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import { ChevronRight, Pencil, Maximize2, Link2, Loader2, FileX2, MapPin, Check, Diff, ExternalLink } from "lucide-react"
import { JournalArticle, authorContactStatusLabelMap, exclusionReasonLabelMap, formatInstitutions, sourceTypeLabelMap, topicLabelMap, updateTopics } from "@/services/journalArticlesService"
import { getReportTypeLabel } from "@/services/reportsService"
import { BiblioCollection, getFilesForSource, GoogleDriveFile, addNote, isCollectionVisibleToUser } from "@/services/biblioService"
import ManageCollectionsDialog from "@/components/journal-articles/ManageCollectionsDialog"
import DetailsSidePanel from "@/components/shared/DetailsSidePanel"
import ExtractStatusBadge from "@/components/journal-articles/ExtractStatusBadge"
import UploadStatusBadge from "@/components/journal-articles/UploadStatusBadge"
import TopicsBadges from "@/components/journal-articles/TopicsBadges"
import { useAuth } from "@/hooks/useAuth"
import { FileButton } from "../shared/FileButton"
import UploadFileDialog from "@/components/journal-articles/UploadFileDialog"
import parse from 'html-react-parser'
import { formatDate } from "@/utils/dateUtils"

/** Props for the BiblioSourceDetailsPanel component. */
interface BiblioSourceDetailsPanelProps {
  /** The article to display. */
  article: JournalArticle
  /** Callback to close the panel. */
  onClose: () => void
  /** List of all collections for mapping collectionId to collectionName. */
  collections: BiblioCollection[]
  /** Callback to update the article in the parent state after edits. */
  onArticleUpdate?: (updated: JournalArticle) => void
  /** Distinct source IDs that have parasite rate data.*/
  parasiteRateSourceIds?: Set<string>
  /**
   * Base route path for the "Open" link and copy-link URL. Defaults to
   * "/articles" (the journal-articles detail route). The All Sources page
   * passes "/all-sources" so the panel links to the generic source detail
   * route instead.
   */
  basePath?: string
  /**
   * Whether the topic editing UI (edit pencil, PR/API/IR/Seasonality toggles)
   * is shown. Topics are an article-specific concept, so the All Sources page
   * passes false to render topics read-only.
   */
  showTopicEditing?: boolean
  /**
   * Singular noun used in labels/aria text (e.g. "article" or "source").
   * Defaults to "article" to preserve existing copy on the journal-articles page.
   */
  entityNoun?: string
  /**
   * Whether the "Topics & Collections" block (topic badges and editing,
   * collection pills, and the Manage Collections dialog) is rendered.
   * Defaults to true. The Reports section passes false because reports
   * have no topics or collections.
   */
  showTopicsAndCollections?: boolean
}

/**
 * Inline side panel displaying full details of a biblio source.
 *
 * Renders all available fields for the selected source in a scrollable
 * panel that sits alongside the main content. Delegates the panel chrome
 * (close button, sticky header, scrollable area) to DetailsSidePanel.
 * Report sources also get a read-only "Report Type" row, and any source
 * with a MAP Survey ID links it to the Survey Catalogue in a new tab.
 *
 * @param props - Component props.
 * @param props.article - The selected source (JournalArticle) to display.
 * @param props.onClose - Callback to close the panel.
 * @param props.collections - List of collections for ID-to-name resolution.
 * @param props.onArticleUpdate - Optional callback receiving the updated source after edits.
 * @param props.parasiteRateSourceIds - Source ids with parasite rate data (shows the map link).
 * @param props.basePath - Base route for the "Open" and copy-link URLs (default "/articles").
 * @param props.showTopicEditing - Whether topic editing controls are shown (default true).
 * @param props.entityNoun - Singular noun used in labels (default "article").
 * @param props.showTopicsAndCollections - Whether the Topics & Collections block
 *   and the Manage Collections dialog are rendered (default true).
 * @returns The rendered details panel.
 */
export default function BiblioSourceDetailsPanel({
  article,
  onClose,
  collections,
  onArticleUpdate,
  parasiteRateSourceIds = new Set(),
  basePath = "/articles",
  showTopicEditing = true,
  entityNoun = "article",
  showTopicsAndCollections = true,
}: BiblioSourceDetailsPanelProps): React.JSX.Element {
  const { getAccessToken, user } = useAuth()
  /** Map of collectionId to collectionName for display. */
  const collectionNameMap = new Map<number, string>(
    collections.map((c) => [c.id, c.collectionName])
  )
  /** Map of collectionId to the full collection record, for membership checks. */
  const collectionById = new Map<number, BiblioCollection>(
    collections.map((c) => [c.id, c])
  )

  const [sourceFiles, setSourceFiles] = useState<GoogleDriveFile[]>([])
  /** Whether source files are currently being fetched from the API. */
  const [filesLoading, setFilesLoading] = useState<boolean>(true)
  const [noteText, setNoteText] = useState<string>("")
  const [noteSubmitting, setNoteSubmitting] = useState<boolean>(false)
  const [localNotes, setLocalNotes] = useState(article.notes ?? [])
  const [editingTopics, setEditingTopics] = useState<boolean>(false)
  const [topicEdits, setTopicEdits] = useState<Record<string, boolean | null>>({})
  const [topicSubmitting, setTopicSubmitting] = useState<boolean>(false)
  const [localTopics, setLocalTopics] = useState({
    PR: article.coversPRTopic ?? null,
    API: article.coversAPITopic ?? null,
    IR: article.coversIRTopic ?? null,
    Seasonality: article.coversSeasonalityTopic ?? null,
  })
  /**
   * Local copy of the article's collection ids so the Collections pills update
   * immediately when the user adds this article to a collection, without
   * waiting for a parent refetch.
   */
  const [localCollectionIds, setLocalCollectionIds] = useState<number[]>(
    article.collectionIds ?? []
  )
  /** Whether the "Manage collections" dialog is open. */
  const [manageCollectionsOpen, setManageCollectionsOpen] = useState<boolean>(false)

  // All available topic keys and their current state from the article
  const ALL_TOPICS = ["PR", "API", "IR", "Seasonality"] as const
  const currentTopicState: Record<string, boolean | null> = localTopics

  // Reset local state when the article changes
  useEffect(() => {
    setLocalNotes(article.notes ?? [])
    setNoteText("")
    setEditingTopics(false)
    setTopicEdits({})
    setLocalCollectionIds(article.collectionIds ?? [])
    setManageCollectionsOpen(false)
    setLocalTopics({
      PR: article.coversPRTopic ?? null,
      API: article.coversAPITopic ?? null,
      IR: article.coversIRTopic ?? null,
      Seasonality: article.coversSeasonalityTopic ?? null,
    })
  }, [article])

  /**
   * Submit a new note via the API and update local state on success.
   */
  const handleAddNote = async (): Promise<void> => {
    if (!noteText.trim() || noteSubmitting) return
    setNoteSubmitting(true)
    try {
      const token = await getAccessToken()
      const createdBy: string = user?.email ?? user?.name ?? "Unknown"
      const response = await addNote(token, article.sourceId, noteText.trim(), createdBy)
      setLocalNotes((prev) => [response.note, ...prev])
      setNoteText("")
    } catch (err) {
      console.error("Failed to add note:", err)
    } finally {
      setNoteSubmitting(false)
    }
  }

  /**
   * Handle a successful collection-membership change: apply the added and
   * removed collection ids to the local list so the pills update immediately,
   * and notify the parent so the cached article list stays in sync.
   *
   * @param addedCollectionIds - Collection ids the article was added to.
   * @param removedCollectionIds - Collection ids the article was removed from.
   */
  const handleCollectionsChanged = (
    addedCollectionIds: number[],
    removedCollectionIds: number[]
  ): void => {
    const removedSet = new Set<number>(removedCollectionIds)
    const applyChanges = (ids: number[]): number[] => {
      const kept = ids.filter((id) => !removedSet.has(id))
      const additions = addedCollectionIds.filter((id) => !kept.includes(id))
      return [...kept, ...additions]
    }

    setLocalCollectionIds((prev) => applyChanges(prev))
    if (onArticleUpdate) {
      const updatedArticle: JournalArticle = {
        ...article,
        collectionIds: applyChanges(article.collectionIds ?? []),
      }
      onArticleUpdate(updatedArticle)
    }
  }

  /**
   * Enter topic editing mode, initialising edits from the current state.
   */
  const startEditingTopics = (): void => {
    setTopicEdits({ ...currentTopicState })
    setEditingTopics(true)
  }

  /**
   * Toggle a topic value in the edit state.
   * If the topic was originally unset (null), cycles: null → true → false → null.
   * If the topic was already set (true/false), only toggles between true and false.
   */
  const toggleTopicEdit = (topic: string): void => {
    setTopicEdits((prev) => {
      const current = prev[topic]
      const original = currentTopicState[topic]
      let next: boolean | null
      if (original === null || original === undefined) {
        // Never labelled — allow cycling through null → true → false → null
        if (current === null || current === undefined) next = true
        else if (current === true) next = false
        else next = null
      } else {
        // Already labelled — only toggle between true and false
        next = !current
      }
      return { ...prev, [topic]: next }
    })
  }

  /**
   * Submit topic edits via the API. Only sends topics that changed.
   */
  const handleSaveTopics = async (): Promise<void> => {
    if (topicSubmitting) return
    // Find topics that actually changed
    const changed: Record<string, boolean> = {}
    for (const topic of ALL_TOPICS) {
      const edited = topicEdits[topic]
      const original = currentTopicState[topic]
      if (edited !== original && edited !== null) {
        changed[topic] = edited
      }
    }
    if (Object.keys(changed).length === 0) {
      setEditingTopics(false)
      return
    }
    setTopicSubmitting(true)
    try {
      const token = await getAccessToken()
      const createdBy: string = user?.email ?? user?.name ?? "Unknown"
      await updateTopics(token, article.sourceId, changed, createdBy)
      // Update local topic state so the UI reflects changes immediately
      setLocalTopics((prev) => {
        const updated = { ...prev }
        for (const [key, value] of Object.entries(changed)) {
          updated[key as keyof typeof updated] = value
        }
        return updated
      })
      // Notify parent so the articles list stays in sync
      if (onArticleUpdate) {
        const newTopicState = {
          PR: changed["PR"] ?? localTopics.PR,
          API: changed["API"] ?? localTopics.API,
          IR: changed["IR"] ?? localTopics.IR,
          Seasonality: changed["Seasonality"] ?? localTopics.Seasonality,
        }
        const updatedArticle: JournalArticle = {
          ...article,
          coversPRTopic: newTopicState.PR ?? article.coversPRTopic,
          coversAPITopic: newTopicState.API ?? article.coversAPITopic,
          coversIRTopic: newTopicState.IR ?? article.coversIRTopic,
          coversSeasonalityTopic: newTopicState.Seasonality ?? article.coversSeasonalityTopic,
          topics: Object.entries(newTopicState)
            .filter(([, v]) => v === true)
            .map(([k]) => k.toLowerCase()),
          nonTopics: Object.entries(newTopicState)
            .filter(([, v]) => v === false)
            .map(([k]) => k.toLowerCase()),
        }
        onArticleUpdate(updatedArticle)
      }
      setEditingTopics(false)
    } catch (err) {
      console.error("Failed to update topics:", err)
    } finally {
      setTopicSubmitting(false)
    }
  }

  /**
   * Fetch the source files from the API for the current article.
   * Called on mount, when the article's source changes, and after a successful file upload.
   */
  const fetchSourceFiles = useCallback(async (): Promise<void> => {
    setSourceFiles([])
    setFilesLoading(true)
    try {
      const token = await getAccessToken()
      const response = await getFilesForSource(token, article.sourceId)
      if (response.files) {
        setSourceFiles(response.files)
      }
    } catch (err) {
      console.error("Failed to load source files:", err)
    } finally {
      setFilesLoading(false)
    }
  }, [getAccessToken, article.sourceId])

  useEffect(() => {
    fetchSourceFiles()
  }, [fetchSourceFiles])

  /**
   * Collection ids whose pill should be shown: those that are public or that
   * the current user is a member of.
   */
  const visibleCollectionIds: number[] = localCollectionIds.filter((id) => {
    const collection = collectionById.get(id)
    if (collection === undefined) {
      return false
    }
    return isCollectionVisibleToUser(collection, user?.sub)
  })

  return (
    <DetailsSidePanel
      onClose={onClose}
      header={
        <div className="min-w-0 flex flex-col items-start gap-1">
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-sm leading-snug">
              {article.title || "Untitled Article"}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">Source ID: {article.sourceId}</p>
          </div>
          <div className="flex gap-2">
                <Button variant="ghost" className="shrink-0 hover:text-foreground! m-0" asChild>
                  <Link
                    to={`${basePath}/${article.sourceId}`}
                    state={{ article }}
                    aria-label={`Open full ${entityNoun}`}
                    className="text-foreground!"
                  >
                    <Maximize2 className="size-3.5" />
                    Open {entityNoun}
                  </Link>
                </Button>
                <CopyLinkButton sourceId={article.sourceId} basePath={basePath} entityNoun={entityNoun} />
          </div>
        </div>
      }
    >
      {/* Scrollable content area */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
      {/* Content */}
      <div className="space-y-4">
        {/* Files */}
        {filesLoading ? (
          <DetailSection label="Files">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="size-3.5 animate-spin" />
              <span>Loading files…</span>
            </div>
          </DetailSection>
        ) : sourceFiles.length > 0 ? (
          <DetailSection label={
            <span className="flex items-center gap-2">
              Files
              <UploadFileDialog sourceId={article.sourceId} onUploadComplete={fetchSourceFiles} />
            </span>
          }>
            <div className="flex gap-2">
            {sourceFiles.map((file) => (
              <FileButton key={file.id} file={file}></FileButton>
            ))}
            </div>
          </DetailSection>
        ) : (
          <DetailSection label={
            <span className="flex items-center gap-2">
              Files
              <UploadFileDialog sourceId={article.sourceId} onUploadComplete={fetchSourceFiles} />
            </span>
          }>
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <FileX2 className="size-3.5" />
              <span>No files found for this source</span>
            </div>
          </DetailSection>
        )}

          {/* Authors */}
          {article.authors?.length > 0 && (
            <DetailSection label="Authors">
              {article.authors.map(a => a.name).join("; ")}
            </DetailSection>
          )}

          {/* Topics & Collections (hidden for sections without topics or collections, e.g. Reports) */}
          {showTopicsAndCollections && (
          <div className="grid grid-cols-2 gap-3">
            <DetailSection
              label={
                <span className="flex items-center gap-1">
                  Topics
                  {showTopicEditing && !editingTopics && (
                    <button
                      type="button"
                      onClick={startEditingTopics}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Edit topics"
                    >
                      <Pencil className="size-3" />
                    </button>
                  )}
                </span>
              }
            >
              {showTopicEditing && editingTopics ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_TOPICS.map(topic => {
                      const value = topicEdits[topic]
                      return (
                        <button
                          key={topic}
                          type="button"
                          onClick={() => toggleTopicEdit(topic)}
                          className={`text-[10px] px-1.5 py-0.5 rounded-md border cursor-pointer transition-colors ${
                            value === true
                              ? "bg-primary text-primary-foreground border-primary"
                              : value === false
                                ? "bg-secondary text-secondary-foreground border-secondary line-through"
                                : "bg-background text-muted-foreground border-dashed"
                          }`}
                        >
                          {topicLabelMap[topic.toLowerCase()] ?? topic}
                        </button>
                      )
                    })}
                  </div>
                  <div className="flex gap-1">
                    <Button size="xs" onClick={handleSaveTopics} disabled={topicSubmitting}>
                      {topicSubmitting ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setEditingTopics(false)}
                      disabled={topicSubmitting}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                (() => {
                  const activeTops = Object.entries(localTopics)
                    .filter(([, v]) => v === true)
                    .map(([k]) => k.toLowerCase())
                  const inactiveTops = Object.entries(localTopics)
                    .filter(([, v]) => v === false)
                    .map(([k]) => k.toLowerCase())
                  return activeTops.length > 0 || inactiveTops.length > 0 ? (
                    <TopicsBadges topics={activeTops} nonTopics={inactiveTops} />
                  ) : (
                    "—"
                  )
                })()
              )}
            </DetailSection>

            <DetailSection
              label={
                <span className="flex items-center gap-2">
                  Collections
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setManageCollectionsOpen(true)}
                    aria-label="Manage this article's collections"
                  >
                    <Diff className="size-3.5" />
                    Manage
                  </Button>
                </span>
              }
            >
              {visibleCollectionIds.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {visibleCollectionIds.map((id, idx) => (
                    <Badge key={idx} variant="outline">
                      {collectionNameMap.get(id) ?? id}
                    </Badge>
                  ))}
                </div>
              ) : (
                "—"
              )}
            </DetailSection>
          </div>
          )}

          {/* Topic History */}
          {article.topicHistory?.length > 0 && (
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground cursor-pointer">
                <ChevronRight className="size-3 transition-transform duration-200 [[data-state=open]>&]:rotate-90" />
                Topic History ({article.topicHistory.length})
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-1.5 mt-1.5">
                  {[...article.topicHistory]
                    .sort((a, b) => b.createdDate.localeCompare(a.createdDate))
                    .map((entry, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-xs text-muted-foreground"
                      >
                        <Badge
                          variant={entry.status ? "default" : "secondary"}
                          className="text-[10px] px-1.5 py-0 shrink-0"
                        >
                          {entry.topic} {entry.status ? "✓" : "✗"}
                        </Badge>
                        <span className="truncate min-w-0">
                          {entry.createdBy === "system:comprehend-classifier"
                            ? "AI Labelled"
                            : entry.createdBy}
                        </span>
                        <span className="ml-auto shrink-0">
                          {new Date(entry.createdDate).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Publication Info */}
          <Separator />
          <div className="grid grid-cols-2 gap-3">
            <DetailSection label="Journal">{article.journaltitle || "—"}</DetailSection>
            <DetailSection label="Year">{article.publicationYear || "—"}</DetailSection>
            <DetailSection label="Volume">{article.volume || "—"}</DetailSection>
            <DetailSection label="Issue">{article.issue || "—"}</DetailSection>
            <DetailSection label="Pages">{article.pages || "—"}</DetailSection>
            <DetailSection label="Source Type">
              {article.sourceType
                ? (sourceTypeLabelMap[article.sourceType] ?? article.sourceType)
                : "—"}
            </DetailSection>
          </div>

          {/* Identifiers */}
          <Separator />
          <div className="grid grid-cols-2 gap-3">
            <DetailSection label="DOI">
              {article.doi ? (
                <a
                  href={article.doiUrl || `https://doi.org/${article.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all"
                >
                  {article.doi}
                </a>
              ) : (
                "—"
              )}
            </DetailSection>
            <DetailSection label="PubMed ID">
              {article.pubmedId ? (
                <a
                  href={article.pubmedUrl || `https://pubmed.ncbi.nlm.nih.gov/${article.pubmedId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {article.pubmedId}
                </a>
              ) : (
                "—"
              )}
            </DetailSection>
            <DetailSection label="IHME NID">{article.ihmeNid || "—"}</DetailSection>
            <DetailSection label="MAP Survey ID">
              {article.mapSurveyId ? (
                // Survey_Link: opens the survey's Survey Catalogue page in a new tab,
                // styled like the survey links in PointDetailCard.
                <Link
                  to={`/survey-catalogue/${encodeURIComponent(article.mapSurveyId)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-2 inline-flex items-center gap-1 hover:text-primary/80"
                  aria-label={`View survey ${article.mapSurveyId} in the survey catalogue (opens in a new tab)`}
                >
                  {article.mapSurveyId}
                  <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                </Link>
              ) : (
                "—"
              )}
            </DetailSection>
            {/* Report Type: reports only, read-only. getReportTypeLabel returns
                "Needs Review" when absent and throws on an unknown value. */}
            {article.sourceType === "report" && (
              <DetailSection label="Report Type">
                {getReportTypeLabel(article.reportType)}
              </DetailSection>
            )}
          </div>

          {/* Abstract */}
          {article.abstract && (
            <>
              <Separator />
              <DetailSection label="Abstract">
                <ExpandableText text={article.abstract} />
              </DetailSection>
            </>
          )}

          {/* Citation */}
          {article.citation && (
            <>
              <Separator />
              <DetailSection label="Citation">
                <p className="text-sm italic">{article.citation}</p>
              </DetailSection>
            </>
          )}

          {/* Suggested Citation */}
          {article.suggestedCitation && (
            <DetailSection label="Suggested Citation">
              <p className="text-sm italic">{article.suggestedCitation}</p>
            </DetailSection>
          )}

          {/* URL */}
          {article.url && (
            <>
              <Separator />
              <DetailSection label="URL">
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-sm"
                >
                  {article.url}
                </a>
              </DetailSection>
            </>
          )}

          {/* Metadata */}
          <Separator />
          <div className="grid grid-cols-2 gap-3">
            <DetailSection label="Survey Type">{article.surveyType || "—"}</DetailSection>
            <DetailSection label="Usage Permission">{article.usagePermission || "—"}</DetailSection>
            <DetailSection label="Created By">
              <span className="break-all">{article.createdBy || "—"}</span>
            </DetailSection>
            <DetailSection label="Created Date">{formatDate(article.createdDate)}</DetailSection>
            <DetailSection label="Last Modified By">
              <span className="break-all">{article.lastModifiedBy || "—"}</span>
            </DetailSection>
            <DetailSection label="Last Modified Date">
              {formatDate(article.lastModifiedDate)}
            </DetailSection>
          </div>

          {/* Research Notes */}
          {article.researchNotes && (
            <>
              <Separator />
              <DetailSection label="Research Notes">
                <p className="text-sm whitespace-pre-wrap">{article.researchNotes}</p>
              </DetailSection>
            </>
          )}

          {/* Contacts / Institutions */}
          {/* formatInstitutions joins report lists and yields "" for an empty list, so it doubles as the presence check */}
          {(article.contacts || article.institution || formatInstitutions(article.institutions)) && (
            <>
              <Separator />
              <div className="grid grid-cols-1 gap-3">
                {article.contacts && (
                  <DetailSection label="Contacts">{article.contacts}</DetailSection>
                )}
                {article.institution && (
                  <DetailSection label="Institution">{article.institution}</DetailSection>
                )}
                {formatInstitutions(article.institutions) && (
                  <DetailSection label="Institutions">{formatInstitutions(article.institutions)}</DetailSection>
                )}
              </div>
            </>
          )}

        {/* Study Years */}
        {article.studyYears && article.studyYears.length > 0 && (
          <>
            <Separator />
            <DetailSection label="Study Years">
              <div className="flex flex-wrap gap-1">
                {article.studyYears.map((year, idx) => (
                  <Badge key={idx} variant="outline">{year}</Badge>
                ))}
              </div>
            </DetailSection>
          </>
        )}

        {/* Date Entities */}
        {article.dateEntities && article.dateEntities.length > 0 && (
          <DetailSection label="Date Entities">
            <div className="flex flex-wrap gap-1">
              {article.dateEntities.map((date, idx) => (
                <Badge key={idx} variant="outline">{date}</Badge>
              ))}
            </div>
          </DetailSection>
        )}

        {/* Continents */}
        {article.continents && article.continents.length > 0 && (
          <>
            <Separator />
            <DetailSection label="Continents">
              <div className="flex flex-wrap gap-1">
                {article.continents.map((continent, idx) => (
                  <Badge key={idx} variant="outline">{continent}</Badge>
                ))}
              </div>
            </DetailSection>
          </>
        )}

        {/* Countries */}
        {article.countries && article.countries.length > 0 && (
          <DetailSection label="Countries">
            <div className="flex flex-wrap gap-1">
              {article.countries.map((country, idx) => (
                <Badge key={idx} variant="outline">{country}</Badge>
              ))}
            </div>
          </DetailSection>
        )}

        {/* Locations (from locationEntities) */}
        {article.locationEntities && article.locationEntities.length > 0 && (
          <>
            <Separator />
            <DetailSection label="Locations">
              <div className="flex flex-wrap gap-1">
                {article.locationEntities.map((location, idx) => (
                  <Badge key={idx} variant="outline">{location}</Badge>
                ))}
              </div>
            </DetailSection>
          </>
        )}

        {/* PR Extraction Status */}
        {(article.prExtractionStatus || article.prUploadStatus || article.prAuthorContactStatus || article.prExclusionReason) && (
          <>
            <Separator />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">PR Extraction Status</p>
            <div className="grid grid-cols-2 gap-3">
              {article.prExtractionStatus != null && (
                <DetailSection label="Extraction Status">
                  <ExtractStatusBadge status={article.prExtractionStatus} />
                </DetailSection>
              )}
              {article.prUploadStatus != null && (
                <DetailSection label="Upload Status">
                  <UploadStatusBadge status={article.prUploadStatus} />
                </DetailSection>
              )}
              {article.prAuthorContactStatus != null && (
                <DetailSection label="Author Contact Status">
                  {authorContactStatusLabelMap[article.prAuthorContactStatus] ?? article.prAuthorContactStatus}
                </DetailSection>
              )}
              {article.prExclusionReason != null && (
                <DetailSection label="Exclusion Reasons">
                  {exclusionReasonLabelMap[article.prExclusionReason] ?? article.prExclusionReason}
                </DetailSection>
              )}
              {article.prExtractionFileId != null && (
                <DetailSection label="Extraction File">
                  <a
                    href={`https://docs.google.com/spreadsheets/d/${article.prExtractionFileId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Extraction Sheet
                  </a>
                </DetailSection>
              )}
            </div>
          </>
        )}

          {/* RS Extraction Status */}
          {(article.rsExtractionStatus || article.rsUploadStatus) && (
            <>
              <Separator />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                RS Extraction Status
              </p>
              <div className="grid grid-cols-2 gap-3">
                {article.rsExtractionStatus != null && (
                  <DetailSection label="Extraction Status">
                    <ExtractStatusBadge status={article.rsExtractionStatus} />
                  </DetailSection>
                )}
                {article.rsUploadStatus != null && (
                  <DetailSection label="Upload Status">
                    <UploadStatusBadge status={article.rsUploadStatus} />
                  </DetailSection>
                )}
                {article.rsExtractionFileId != null && (
                  <DetailSection label="Extraction File">
                    <a
                      href={`https://docs.google.com/spreadsheets/d/${article.rsExtractionFileId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Extraction Sheet
                    </a>
                  </DetailSection>
                )}
              </div>
            </>
          )}

        {parasiteRateSourceIds.has(String(article.sourceId)) && (
          <>
            <Separator />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Parasite Rate</p>
            <Button variant="outline" size="sm" asChild className="w-fit">
              <Link
                to={`/data/parasite-rate/clusters?sources=${encodeURIComponent(String(article.sourceId))}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`View source ${article.sourceId} on the parasite rate map (opens in a new tab)`}
              >
                <MapPin data-icon="inline-start" />
                View on parasite rate map
              </Link>
            </Button>
          </>
        )}

        {/* Notes */}
        {(localNotes.length > 0 || true) && (
          <>
            <Separator />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notes</p>
            {localNotes.length > 0 && (
              <div className="space-y-2">
                {[...localNotes]
                  .sort((a, b) => b.createdDate.localeCompare(a.createdDate))
                  .map((note, idx) => (
                  <div key={idx} className="rounded border p-2 text-xs">
                    <p className="whitespace-pre-wrap">{note.noteText}</p>
                    <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                      <span className="truncate min-w-0">{note.createdBy}</span>
                      <span className="ml-auto shrink-0">{new Date(note.createdDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Textarea
                placeholder="Add a note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className="text-xs min-h-[60px] resize-none"
              />
            </div>
            <Button
              size="sm"
              onClick={handleAddNote}
              disabled={!noteText.trim() || noteSubmitting}
              className="w-full"
            >
              {noteSubmitting ? "Adding..." : "Add Note"}
            </Button>
          </>
        )}
      </div>
      </div>

      {/* Manage-collections dialog, driven by the Collections section button. */}
      {showTopicsAndCollections && (
      <ManageCollectionsDialog
        open={manageCollectionsOpen}
        sourceId={article.sourceId}
        collections={collections}
        existingCollectionIds={localCollectionIds}
        onClose={() => setManageCollectionsOpen(false)}
        onChanged={(result) =>
          handleCollectionsChanged(
            result.addedCollectionIds,
            result.removedCollectionIds
          )
        }
      />
      )}
    </DetailsSidePanel>
  )
}

/**
 * Button that copies the article link to clipboard and shows a checkmark
 * for 2 seconds to confirm the action.
 *
 * @param props - Component props.
 * @param props.sourceId - The article's source ID used to build the URL.
 */
function CopyLinkButton({
  sourceId,
  basePath = "/articles",
  entityNoun = "article",
}: {
  sourceId: number
  basePath?: string
  entityNoun?: string
}): React.JSX.Element {
  const [copied, setCopied] = useState<boolean>(false)

  /**
   * Copy the source URL to clipboard and show a confirmation check.
   */
  const handleCopy = (): void => {
    const url = `${window.location.origin}${basePath}/${sourceId}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button
      variant="ghost"
      className="shrink-0 underline underline-offset-2"
      aria-label={`Copy link to ${entityNoun}`}
      onClick={handleCopy}
    >
      {copied ? <Check className="size-3.5 text-green-600" /> : <Link2 className="size-3.5" />}
      {copied ? "Link copied!" : `Copy ${entityNoun}'s link`}
    </Button>
  )
}

/**
 * Helper component for rendering a labelled detail field.
 *
 * @param props - Component props.
 * @param props.label - The field label.
 * @param props.children - The field value content.
 */
function DetailSection({ label, children }: { label: React.ReactNode; children: React.ReactNode }): React.JSX.Element {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground mb-0.5">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  )
}

/**
 * Helper component for displaying long text with a truncated preview
 * and an expand/collapse button.
 *
 * @param props - Component props.
 * @param props.text - The full text content to display.
 */
function ExpandableText({ text }: { text: string }): React.JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(false)

  return (
    <div>
      <p className={`text-sm leading-relaxed whitespace-pre-wrap ${!expanded ? "line-clamp-4" : ""}`}>
        {parse(text)}
      </p>
      <Button
        variant="link"
        size="sm"
        className="px-0 h-auto text-xs text-muted-foreground"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "Show less" : "Show more"}
      </Button>
    </div>
  )
}
